import { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

export const useZones = () => {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setZones([]);
      setIsLoading(false);
      return;
    }

    const fetchZones = async (isInitial = true) => {
      // Only abort if this is a background update (not initial)
      if (!isInitial && abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Only show loading spinner on initial fetch
        if (isInitial) {
          setIsLoading(true);
        }
        setError(null);

        const { data: zonesData, error: zonesError } = await supabase
          .from('zones')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true });

        if (zonesError) {
          throw zonesError;
        }

        // Only update state if this request wasn't aborted
        if (abortController.signal.aborted) {
          return;
        }

        setZones((zonesData || []).map(z => ({
          id: z.id,
          name: z.name,
          description: z.description || '',
          color: z.color || '#0f62fe',
          user_id: z.user_id,
          sensor_ids: z.sensor_ids || [],
          gateway_ids: z.gateway_ids || [],
          type: z.type || 'room',
          custom_type: z.custom_type,
          zone_type: z.zone_type || 'office',
          street_address: z.street_address,
          city: z.city,
          state: z.state,
          zip_code: z.zip_code,
          country: z.country,
          latitude: z.latitude,
          longitude: z.longitude,
          address_formatted: z.address_formatted,
          created_at: z.created_at,
          updated_at: z.updated_at
        })));
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }
        console.error('Error fetching zones:', err);
        setError(err.message || 'Failed to fetch zones');
      } finally {
        if (isInitial && !abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Initial fetch
    fetchZones(true);

    // Debounced update function for real-time changes
    const debouncedUpdate = () => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new timer to fetch after 1 second of no changes
      debounceTimerRef.current = setTimeout(() => {
        fetchZones(false); // Don't show loading spinner on background updates
      }, 1000);
    };

    // Set up real-time subscription for zones
    const subscription = supabase
      .channel('zones_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'zones',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Debounced update to prevent rapid re-renders
          debouncedUpdate();
        }
      )
      .subscribe();

    return () => {
      // Clean up subscription
      subscription.unsubscribe();

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user, isAuthenticated]);

  const addZone = async (zone) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase
        .from('zones')
        .insert([{
          ...zone,
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error adding zone:', err);
      setError(err.message || 'Failed to add zone');
      throw err; // Re-throw so calling component can handle it
    }
  };

  const updateZone = async (id, updates) => {
    try {
      const { error } = await supabase
        .from('zones')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating zone:', err);
      setError(err.message || 'Failed to update zone');
    }
  };

  const deleteZone = async (id) => {
    try {
      const { error } = await supabase
        .from('zones')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
    } catch (err) {
      console.error('Error deleting zone:', err);
      setError(err.message || 'Failed to delete zone');
    }
  };

  return {
    zones,
    isLoading,
    error,
    addZone,
    updateZone,
    deleteZone
  };
};

